import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Scroll from 'react-scroll';
import { e4Format, e4Formats, isSameDay } from 'helpers/dates';
import { apiDelete, apiGet, apiPost } from 'services';
import { ArrayHelpers, Forms, Loading, Modal, StringHelpers, SystemText } from '@salesperformanceint/e4shared';

import './setHistoricGoals.css';
import { setShowSuccess } from 'actions/ui';
import { weekStarts } from 'helpers/goals';

export const SetHistoricGoals = ({ goal, profile, onClose, onReload }) => {
    const dispatch = useDispatch();

    const [error, setError] = React.useState('');
    const [values, setValues] = React.useState([]);
    const [date, setDate] = React.useState(null);
    const [value, setValue] = React.useState(null);
    const [saving, setSaving] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const user = useSelector(state => state.user.user);

    const id = (profile || user)?.id;

    const api = {
        load: () => {
            setLoading(true);
            apiGet(`quota-values/historic/${goal.id}/${id}`, user.guid, response => {
                setValues(response);
                setLoading(false);
            });
        },
        add: () => {
            setSaving(true);
            if (validate()) {
                const valFormat = goal.format === 'P' ? value / 100 : value;
                const newGoal = { valid_For: date, value: valFormat };
                apiPost(`quota-value/${goal.id}/${id}`, newGoal, user.guid, () => {
                    dispatch(setShowSuccess(text.success.added));
                    setSaving(false);
                    api.load();
                    onReload?.();
                });
            } else {
                setSaving(false);
            }
        },
        delete: validFor => {
            apiDelete(`quota-value/${goal.id}/${id}?for=${validFor}`, null, user.guid, () => {
                dispatch(setShowSuccess(text.success.deleted));
                api.load();
                onReload?.();
            });
        },
    };

    React.useEffect(() => {
        goal?.id && api.load();
    }, [goal?.id]);

    if (!goal) return null;

    const validate = () => {
        let error = '';

        if (!date) {
            error = text.errors.noDate;
        } else if (values.some(v => isSameDay(v.valid_For, date))) {
            error = text.errors.invalidDate;
        } else if (!value && value !== 0) {
            error = text.errors.noValue;
        } else if (isNaN(value) || value < 0) {
            error = text.errors.invalidValue;
        }

        setError(error);

        return !error;
    };

    const closeModal = back => {
        onClose?.(back);
        onReload?.();
        setValue(null);
        setDate(null);
        setError('');
        setSaving(false);
    };

    const isMe = user.id === id;
    const isAdmin = !id;

    const text = {
        title: {
            admin: SystemText('Goals/Historic/Title/Admin', goal?.name),
            you: SystemText('Goals/Historic/Title/You', goal?.name),
            them: SystemText('Goals/Historic/Title/Them', profile?.first_Name, goal?.name),
        },
        table: {
            validFrom: SystemText('Goals/Historic/Table/ValidFrom'),
            value: SystemText('Goals/Historic/Table/Value'),
        },
        noResults: SystemText('Goals/Historic/NoResults', goal.name),
        addNew: SystemText('Goals/Historic/Add'),
        seconds: SystemText('Goals/Historic/Seconds'),
        success: {
            added: SystemText('Goals/Historic/Success/Added'),
            deleted: SystemText('Goals/Historic/Success/Deleted'),
        },
        errors: {
            noDate: SystemText('Goals/Historic/Errors/NoDate'),
            invalidDate: SystemText('Goals/Historic/Errors/InvalidDate'),
            noValue: SystemText('Goals/Historic/Errors/NoValue'),
            invalidValue: SystemText('Goals/Historic/Errors/InvaidValue'),
        },
        buttons: {
            back: SystemText('Goals/Historic/Buttons/Back'),
            close: SystemText('Goals/Historic/Buttons/Close'),
        },
    };

    const extraFormat = goal.format === 'S' ? ` (${text.seconds})` : '';

    const footer = (
        <div className="flex">
            {!isAdmin && (
                <div className="flex-50 text-left">
                    <button data-testid="modal-button-back" className="btn btn-link" onClick={() => closeModal(true)}>
                        {text.buttons.back}
                    </button>
                </div>
            )}
            <div className={!isAdmin ? 'flex-50 text-right' : 'flex-grow text-right'}>
                <button data-testid="modal-button-close" className="btn" onClick={() => closeModal(false)}>
                    {text.buttons.close}
                </button>
            </div>
        </div>
    );

    const setDateByPeriod = d => {
        if (goal.period === 'Y') {
            const fy = new Date(user.fy_Start_Date);
            const temp = new Date(new Date(d).getFullYear(), fy.getMonth(), 1);
            setDate(temp);
        } else {
            setDate(d);
        }
    };

    return (
        <Modal
            show={goal}
            title={isAdmin ? text.title.admin : isMe ? text.title.you : text.title.them}
            close={() => closeModal(false)}
            footer={footer}
        >
            <Scroll.Element name="top-of-modal" />
            <div className="form-group" id="manage-goals-form" style={{ minHeight: '240px' }}>
                {loading ? (
                    <Loading />
                ) : (
                    <table className="table table-condensed table-hover">
                        <thead>
                            <tr key="row-existing-headers">
                                <th width="25%">{text.table.validFrom}</th>
                                <th width="25%">
                                    {text.table.value}
                                    {extraFormat}
                                </th>
                                <th width="5%"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {ArrayHelpers.existsAndHasLength(values) ? (
                                values.map((v, i) => {
                                    return (
                                        <tr key={`row-${i}`}>
                                            <td className="inline-form">{e4Format(v.valid_For, e4Formats.long)}</td>
                                            <td>
                                                <div className="flex flex-center">
                                                    {StringHelpers.formatMetric(goal.format, v.value, user.currencySymbol)}
                                                </div>
                                            </td>
                                            <td className="text-right">
                                                <button
                                                    data-testid="button-goals-delete"
                                                    className="btn btn-xs btn-danger"
                                                    onClick={() => api.delete(v.valid_For)}
                                                >
                                                    <i className="fa fa-trash" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="3">{text.noResults}</td>
                                </tr>
                            )}
                            <tr key="row-add-header">
                                <th className="inline-form" colSpan="3">
                                    <div className="mt20">{text.addNew}</div>
                                </th>
                            </tr>
                            <tr key="row-add-headers">
                                <th className="inline-form">{text.table.validFrom}</th>
                                <th>
                                    <div className="flex flex-center">{text.table.value}</div>
                                </th>
                                <th className="text-right"></th>
                            </tr>
                            <tr key="row-add">
                                <td className="inline-form">
                                    <Forms.Date
                                        data-testid="date-goals"
                                        extraProps={{
                                            showYearPicker: goal.period === 'Y',
                                            showQuarterYearPicker: goal.period === 'Q',
                                            showMonthYearPicker: goal.period === 'M',
                                            includeDates: goal.period === 'W' ? weekStarts() : undefined,
                                        }}
                                        value={date}
                                        onChange={setDateByPeriod}
                                    />
                                </td>
                                <td>
                                    <div className="flex flex-center text-input-holder">
                                        <input
                                            data-testid="input-goals"
                                            value={value}
                                            onChange={e => setValue(parseInt(e.target.value))}
                                            type="number"
                                            className="form-control"
                                        />
                                        {goal.format === 'P' && (
                                            <span className="lm10">
                                                <span
                                                    className="form-control"
                                                    style={{ borderLeft: '0px', backgroundColor: 'rgb(238, 238, 238)' }}
                                                >
                                                    %
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="text-right">
                                    <button
                                        data-testid="button-goals-add"
                                        className="btn btn-xs btn-success"
                                        onClick={() => api.add()}
                                        disabled={saving}
                                    >
                                        <i className="fa fa-save" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                )}
                {error && (
                    <div data-testid="error-goals" className="ml5 mt20 mb10 text-danger">
                        {error}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default SetHistoricGoals;
